/**
 * Julkinen kuitin lähetyssivu (ei autentikointia).
 * Käytetään, kun käyttäjä (kumppani, ulkopuolinen) avaa jakolinkin /scan/:token.
 * 1) Linkki validoidaan
 * 2) Käyttäjä lataa / ottaa kuvan kuitistaa
 * 3) AI lukee tiedot, käyttäjä tarkistaa
 * 4) Lähetys tallentuu jakolinkin omistajalle automaattisesti (+ kirjanpitoon)
 */
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  Camera, Upload, Sparkles, Loader2, CheckCircle, X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const eur = (v) => new Intl.NumberFormat("fi-FI", { style: "currency", currency: "EUR" }).format(Number(v || 0));
const today = () => new Date().toISOString().slice(0, 10);

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

function recalc(form, key) {
  const gross = parseFloat(form.gross_amount) || 0;
  const net = parseFloat(form.net_amount) || 0;
  const rate = parseFloat(form.vat_rate) || 0;
  const next = { ...form };
  if (key === "gross_amount" || key === "vat_rate") {
    if (gross > 0) {
      const n = +(gross / (1 + rate / 100)).toFixed(2);
      next.net_amount = n;
      next.vat_amount = +(gross - n).toFixed(2);
    }
  } else if (key === "net_amount") {
    if (net > 0) {
      const v = +((net * rate) / 100).toFixed(2);
      next.vat_amount = v;
      next.gross_amount = +(net + v).toFixed(2);
    }
  }
  return next;
}

export default function PublicReceiptScan() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imageBase64, setImageBase64] = useState(null);
  const [imageMime, setImageMime] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    vendor: "",
    vendor_business_id: "",
    receipt_date: today(),
    receipt_number: "",
    gross_amount: "",
    net_amount: "",
    vat_amount: "",
    vat_rate: 24,
    payment_method: "card",
    category: "Muu",
    description: "",
    notes: "",
  });
  const fileRef = useRef(null);
  const camRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${BACKEND}/api/ereceipts/share/${token}`);
        setInfo(r.data);
      } catch (e) {
        setError(e.response?.data?.detail || "Linkki ei kelpaa tai on vanhentunut");
      }
      setLoading(false);
    })();
  }, [token]);

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      alert("Kuva on liian iso (max 10 MB)");
      return;
    }
    const b64 = await fileToBase64(file);
    setImagePreview(b64);
    setImageBase64(b64);
    setImageMime(file.type || "image/jpeg");
  };

  const runOcr = async () => {
    if (!imageBase64) return;
    setOcrLoading(true);
    try {
      const r = await axios.post(`${BACKEND}/api/ereceipts/share/${token}/ocr`, {
        image_base64: imageBase64,
        mime: imageMime,
      });
      const o = r.data;
      setForm((f) => ({
        ...f,
        vendor: o.vendor || f.vendor,
        vendor_business_id: o.vendor_business_id || f.vendor_business_id,
        receipt_date: o.receipt_date || f.receipt_date,
        receipt_number: o.receipt_number || f.receipt_number,
        gross_amount: o.gross_amount || f.gross_amount,
        net_amount: o.net_amount || f.net_amount,
        vat_amount: o.vat_amount || f.vat_amount,
        vat_rate: o.vat_rate ?? f.vat_rate,
        payment_method: o.payment_method || f.payment_method,
        category: o.suggested_category || f.category,
        description: o.notes || f.description,
      }));
    } catch (e) {
      alert("OCR epäonnistui: " + (e.response?.data?.detail || e.message));
    }
    setOcrLoading(false);
  };

  const submit = async () => {
    if (!imageBase64) {
      alert("Lisää ensin kuva kuitista");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND}/api/ereceipts/share/${token}/submit`, {
        image_base64: imageBase64,
        mime: imageMime,
        receipt: {
          ...form,
          gross_amount: parseFloat(form.gross_amount) || 0,
          net_amount: parseFloat(form.net_amount) || 0,
          vat_amount: parseFloat(form.vat_amount) || 0,
          vat_rate: parseFloat(form.vat_rate) || 0,
        },
      });
      setDone(true);
    } catch (e) {
      alert("Lähetys epäonnistui: " + (e.response?.data?.detail || e.message));
    }
    setSubmitting(false);
  };

  const update = (k, v) => setForm((f) => {
    const next = { ...f, [k]: v };
    if (["gross_amount", "net_amount", "vat_rate"].includes(k)) return recalc(next, k);
    return next;
  });

  if (loading) {
    return (
      <div className="min-h-screen app-bg flex items-center justify-center text-white">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen app-bg flex items-center justify-center px-4">
        <div className="glass-card p-8 max-w-md text-center">
          <X className="w-12 h-12 text-red-400 mx-auto mb-3" />
          <h1 className="text-xl font-display text-white mb-2">Linkki ei kelpaa</h1>
          <p className="text-white/60 text-sm">{error}</p>
        </div>
      </div>
    );
  }
  if (done) {
    return (
      <div className="min-h-screen app-bg flex items-center justify-center px-4">
        <div className="glass-card p-8 max-w-md text-center" data-testid="public-scan-success">
          <CheckCircle className="w-16 h-16 text-emerald-400 mx-auto mb-3" />
          <h1 className="text-2xl font-display text-white mb-2">Kuitti vastaanotettu</h1>
          <p className="text-white/60 mb-4">Kiitos! Kuittisi on tallentunut {info?.owner_name || info?.owner_email}-tilille.</p>
          <Button onClick={() => {
            setDone(false);
            setImageBase64(null);
            setImagePreview(null);
            setForm({ ...form, vendor: "", gross_amount: "", net_amount: "", vat_amount: "", description: "" });
          }} className="btn-luxe">
            Lähetä uusi kuitti
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen app-bg px-4 py-8" data-testid="public-scan-page">
      <div className="max-w-3xl mx-auto">
        <header className="text-center mb-6">
          <p className="text-[10px] uppercase tracking-[0.3em] text-amber-300">E-Kuitti</p>
          <h1 className="text-3xl font-display gradient-text-luxe">Lähetä kuitti</h1>
          {info && (
            <p className="text-white/60 text-sm mt-2">
              {info.label && <span className="text-white/80">{info.label} · </span>}
              vastaanottaja: <strong>{info.owner_name || info.owner_email}</strong>
            </p>
          )}
        </header>

        <div className="glass-card p-6 space-y-4">
          {/* Image upload */}
          <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 min-h-[200px] flex flex-col items-center justify-center text-center">
            {imagePreview ? (
              <img src={imagePreview} alt="Kuitti" className="max-h-64 rounded-lg" />
            ) : (
              <>
                <Camera className="w-10 h-10 mx-auto text-white/30 mb-2" />
                <p className="text-sm text-white/60">Ota kamerakuva tai lataa kuva kuitistasi</p>
              </>
            )}
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={(e) => handleFile(e.target.files?.[0])}
            className="hidden"
          />
          <input
            ref={camRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={(e) => handleFile(e.target.files?.[0])}
            className="hidden"
            data-testid="public-scan-camera"
          />
          <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-2" data-testid="public-scan-upload-btn">
              <Upload className="w-4 h-4" /> Lataa
            </Button>
            <Button variant="outline" onClick={() => camRef.current?.click()} className="inline-flex items-center gap-2">
              <Camera className="w-4 h-4" /> Kamera
            </Button>
          </div>

          <Button
            onClick={runOcr}
            disabled={!imageBase64 || ocrLoading}
            className="btn-luxe w-full inline-flex items-center gap-2"
            data-testid="public-scan-ocr-btn"
          >
            {ocrLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {ocrLoading ? "Lukee tiedot..." : "Lue tiedot AI:lla"}
          </Button>

          {/* Form (näkyy aina, AI vain täyttää) */}
          <div className="border-t border-white/5 pt-4 space-y-3">
            <SimpleField label="Myyjä" value={form.vendor} onChange={(v) => update("vendor", v)} testid="public-scan-vendor" />
            <div className="grid grid-cols-2 gap-3">
              <SimpleField label="Päivä" type="date" value={form.receipt_date} onChange={(v) => update("receipt_date", v)} testid="public-scan-date" />
              <SimpleField label="Kuitti #" value={form.receipt_number} onChange={(v) => update("receipt_number", v)} />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <SimpleField label="Brutto €" type="number" value={form.gross_amount} onChange={(v) => update("gross_amount", v)} testid="public-scan-gross" />
              <SimpleField label="Veroton €" type="number" value={form.net_amount} onChange={(v) => update("net_amount", v)} />
              <div>
                <Label className="text-xs">ALV %</Label>
                <select
                  value={form.vat_rate}
                  onChange={(e) => update("vat_rate", e.target.value)}
                  className="w-full mt-1 bg-input/40 border border-white/10 rounded-lg px-3 py-2 text-sm"
                >
                  {[0, 10, 14, 24, 25.5].map((r) => (<option key={r} value={r}>{r} %</option>))}
                </select>
              </div>
            </div>
            <p className="text-xs text-violet-300 -mt-1">ALV-summa: {eur(form.vat_amount)}</p>
            <div>
              <Label className="text-xs">Kategoria</Label>
              <select
                value={form.category}
                onChange={(e) => update("category", e.target.value)}
                className="w-full mt-1 bg-input/40 border border-white/10 rounded-lg px-3 py-2 text-sm"
                data-testid="public-scan-category"
              >
                {(info?.categories || ["Muu"]).map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Selite</Label>
              <textarea
                value={form.description}
                onChange={(e) => update("description", e.target.value)}
                className="w-full mt-1 bg-input/40 border border-white/10 rounded-lg px-3 py-2 text-sm min-h-[70px]"
                placeholder="Lyhyt kuvaus mihin kuitti liittyy..."
                data-testid="public-scan-description"
              />
            </div>
          </div>

          <Button
            onClick={submit}
            disabled={submitting || !imageBase64}
            className="btn-luxe w-full"
            data-testid="public-scan-submit"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Lähetä kuitti
          </Button>
        </div>

        <p className="text-center text-[10px] text-white/30 mt-6">
          Apps2Go Hub · E-Kuitti
        </p>
      </div>
    </div>
  );
}

function SimpleField({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <Input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        step={type === "number" ? "0.01" : undefined}
        className="mt-1"
        data-testid={testid}
      />
    </div>
  );
}
